import {getNonce, getSticky} from '../auth/auth';
import {ApiError, ErrorCode, FormDataAbortedError, ServerEnvelope} from "../types/Error";
import {navigate} from "../GlobalNavigate";
import {ErrorParam} from "../components/pages/ErrorPage";
import {PAGES_PATHS} from "../Entrypoint";

export function formData<T>(
		path: string,
		form: FormData,
		onProgress?: (loaded: number, total: number) => void,
): { promise: Promise<T>; abort: () => void } {
	const xhr = new XMLHttpRequest();

	const formattedPath = prepareRequest(path);
	const loggablePath = formattedPath.substring(0, formattedPath.indexOf("?"))
	const promise = new Promise<T>((resolve, reject) => {
		if (onProgress) {
			xhr.upload.onprogress = (e) => {
				if (e.lengthComputable) {
					onProgress(e.loaded, e.total);
				}
			};
		}

		xhr.onload = () => {
			const status = xhr.status;
			const raw = xhr.responseText ?? '';
			let parsed: ServerEnvelope<T> | undefined;
			try {
				parsed = raw.trim() ? JSON.parse(raw) : undefined;
			} catch {
				parsed = undefined;
			}

			if (status === 200) {
				const data =
						parsed && Object.prototype.hasOwnProperty.call(parsed, 'data')
								? (parsed.data as T)
								: (parsed as unknown as T);
				resolve(data);
			} else if (status === 401) {
				navigate(`/${PAGES_PATHS.ERROR}?${ErrorParam.KEY}=${ErrorCode.UNAUTHORIZED}`, {replace: true});
			} else {
				const code = parsed?.error?.code ?? ErrorCode.UNKNOWN_REASON;
				const details = parsed?.error?.details;
				console.error(`received status ${status} from request to ${loggablePath}. code: ${code}, details: ${details}`)

				reject(
						new ApiError({
							status,
							code,
							details,
							response: undefined,
							body: parsed ?? raw
						})
				);
			}
		};

		xhr.onerror = (e) => {
			console.error(`error handling request to ${loggablePath}`, e)
			reject(
					new ApiError({
						status: -1,
						code: ErrorCode.NETWORK_ERROR,
						body: undefined
					})
			);
		};

		xhr.onabort = () => reject(new FormDataAbortedError());

		xhr.open('POST', formattedPath, true);
		xhr.withCredentials = true;

		const nonce = getNonce();
		if (nonce) {
			xhr.setRequestHeader('X-Nonce', nonce);
		}

		xhr.send(form);
	});

	return {
		promise,
		abort: () => xhr.abort()
	};
}

export const get = <T>(path: string, abortSignal?: AbortSignal): Promise<T> => {
	return sendRequest<T>('GET', path, undefined, abortSignal);
}

export const post = <T>(path: string, request?: unknown, abortSignal?: AbortSignal): Promise<T> => {
	return sendRequest<T>('POST', path, request, abortSignal);
}

export const put = <T>(path: string, request?: unknown, abortSignal?: AbortSignal): Promise<T> => {
	return sendRequest<T>('PUT', path, request, abortSignal);
}

export const patch = <T>(path: string, request?: unknown, abortSignal?: AbortSignal): Promise<T> => {
	return sendRequest<T>('PATCH', path, request, abortSignal);
}

export const del = <T>(path: string, request?: unknown, abortSignal?: AbortSignal): Promise<T> => {
	return sendRequest<T>('DELETE', path, request, abortSignal);
}

const sendRequest = async <T>(
		method: string,
		path: string,
		request?: unknown,
		abortSignal?: AbortSignal
): Promise<T> => {
	try {
		return await _sendRequest(method, path, request, abortSignal);
	} catch (e) {
		if (e instanceof ApiError) {
			throw e;
		}

		console.error(`error handling request to ${path}`, e)
		throw new ApiError({
			status: -1,
			code: ErrorCode.UNKNOWN_REASON,
		});
	}
}

const _sendRequest = async <T>(
		method: string,
		path: string,
		request?: unknown,
		abortSignal?: AbortSignal
): Promise<T> => {
	const headers: Record<string, string> = {'Content-Type': 'application/json'};

	const nonce = getNonce();
	if (nonce) headers['X-Nonce'] = nonce;

	const options: RequestInit = {
		method,
		headers,
		body: request === undefined ? undefined : JSON.stringify(request),
		signal: abortSignal
	};

	let response: Response | undefined;
	const formattedPath = prepareRequest(path);
	const loggablePath = formattedPath.substring(0, formattedPath.indexOf("?"))
	try {
		response = await fetch(new Request(formattedPath), options as any);
	} catch (e: any) {
		console.error(`error handling request to ${loggablePath}`, e)
		throw new ApiError({
			status: -1,
			code: e?.code ?? ErrorCode.NETWORK_ERROR,
			body: undefined
		});
	}

	const rawText = await response.text();
	const hasBody = rawText.trim().length > 0;
	let parsed: Partial<ServerEnvelope<T>> | undefined;
	try {
		parsed = hasBody ? JSON.parse(rawText) : undefined;
	} catch {
		parsed = undefined;
	}

	if (response.status === 200) {
		return parsed && Object.prototype.hasOwnProperty.call(parsed, 'data')
				? (parsed.data as T)
				: (parsed as unknown as T);
	}

	if (response.status == 401) {
		navigate(`/${PAGES_PATHS.ERROR}?${ErrorParam.KEY}=${ErrorCode.UNAUTHORIZED}`, {replace: true});
	}

	const code = parsed?.error?.code ?? ErrorCode.UNKNOWN_REASON;
	const details = parsed?.error?.details;

	console.error(`received status ${response.status} from request to ${loggablePath}. code: ${code}, details: ${details}`)
	throw new ApiError({
		status: response.status,
		code,
		details,
		response,
		body: parsed ?? rawText
	});
};

export function prepareRequest(path: string): string {
	const base = getContextPath().replace(/\/+$/, ""); // remove trailing slashes
	const cleanedPath = path.replace(/^\/+/, ""); // remove leading slashes

	let url = `${base}/${cleanedPath}`;

	const sticky = getSticky();
	if (sticky) {
		url += (url.includes("?") ? "&" : "?") + `sticky=${encodeURIComponent(sticky)}`;
	}

	return url;
}

export function getContextPath(): string {
	if (process.env.NODE_ENV == "development") {
		const proxyUrl = process.env.REACT_APP_UPLOAD;
		return proxyUrl!.substring(0, proxyUrl!.lastIndexOf("/upload"));
	} else {
		const currentLocation = location.href;
		return (
				currentLocation.substring(0, currentLocation.lastIndexOf("/upload"))
		);
	}
}
